import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { Grid } from "@material-ui/core";
import CustomModal from "../../components/common/customModal";
import CustomInput from "../../components/common/inputField";
import CustomSelect from "../../components/common/customSelect";
import FileUpload from "../../components/common/fileUpload";
import CustomDatePicker from "../../components/common/customDatePicker";
import dayjs, { Dayjs } from "dayjs";
import dashboardService from "../../services/DashboardServices";
import { showToast } from "../../components/common/toastNotification";
import { JobPosting, JobPostingErrors } from "../../types/dashboard";

interface JobPostingModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  jobUpdateData?: JobPosting;
}

const InternalJobPostingModal: React.FC<JobPostingModalProps> = ({
  open,
  onClose,
  onSave,
  jobUpdateData,
}) => {
  const [formData, setFormData] = useState<JobPosting>({
    id: 0,
    driveLink: "",
    jobOpeningTitle: "",
    jobDesignation: "",
    jobOpeningLocationId: 0,
    jobOpeningLocation: "",
    expirationDate: "",
    emailId: "",
    file: null,
    isLink: false,
  });

  const [locations, setLocations] = useState<
    { value: number; label: string }[]
  >([]);
  const [formErrors, setFormErrors] = useState<JobPostingErrors>({});
  const [btnLoading, setBtnLoading] = useState(false);

  useEffect(() => {
    fetchLocations();
  }, []);

  /// fetch the location list
  const fetchLocations = async () => {
    try {
      const response = await dashboardService.getLocationMasterAPI();
      if (response?.data?.success && response?.data?.data) {
        const formattedLocations = response.data.data.map(
          (location: { locationId: number; locationName: string }) => ({
            value: location.locationId,
            label: location.locationName,
          })
        );
        setLocations(formattedLocations);
      }
    } catch (error) {
      showToast("Failed to load locations.", "error");
    }
  };

  useEffect(() => {
    if (jobUpdateData) {
      const isYouTube = jobUpdateData?.isLink;
      setFormData({
        id: jobUpdateData.id || 0,
        driveLink: jobUpdateData.driveLink || "",
        jobOpeningTitle: jobUpdateData.jobOpeningTitle || "",
        jobDesignation: jobUpdateData.jobDesignation || "",
        jobOpeningLocationId: jobUpdateData.jobOpeningLocationId || 0,
        jobOpeningLocation: jobUpdateData.jobOpeningLocation || "",
        expirationDate: jobUpdateData.expirationDate
          ? dayjs(jobUpdateData.expirationDate).format("YYYY-MM-DD")
          : "",
        emailId: jobUpdateData.emailId || "",
        file: null,
        isLink: isYouTube || false,
      });
    }
  }, [jobUpdateData]);

  const handleChange = <T extends keyof JobPosting>(
    field: T,
    value: JobPosting[T]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (value === null) {
      setFormData((prev) => ({
        ...prev,
        ["driveLink"]: "",
      }));
    }
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [field]: "",
      fileError: "",
    }));
  };

  // Inside validateForm
  const validateForm = () => {
    const errors: JobPostingErrors = {};

    if (!formData.jobOpeningTitle?.trim())
      errors.jobOpeningTitle = "Job title is required.";

    if (!formData.jobDesignation?.trim())
      errors.jobDesignation = "Job designation is required.";

    if (!formData.jobOpeningLocationId)
      errors.jobOpeningLocationId = "Please select a location.";

    if (!formData.expirationDate)
      errors.expirationDate = "Expiration date is required.";

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.emailId && !emailRegex.test(formData.emailId)) {
      errors.emailId = "Enter a valid email.";
    }

    const driveLinkUrl = formData.driveLink?.trim() || "";
    const driveLinkPattern = /^(https?:\/\/)?([^\s]+)$/;

    if (formData.file && driveLinkUrl) {
      errors.driveLink =
        "Please provide either a Drive Link or a File, not both.";
    } else if (!formData.file && !driveLinkUrl) {
      errors.driveLink = "Either upload a file or provide a Drive Link.";
    } else if (
      driveLinkUrl !== "" &&
      !driveLinkPattern.test(driveLinkUrl) &&
      formData.isLink
    ) {
      errors.driveLink = "Please enter a valid Google Drive link.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveData = async () => {
    if (!validateForm()) return;

    setBtnLoading(true);
    const data = new FormData();

    if (formData.id) data.append("id", String(formData.id));
    data.append(
      "driveLink",
      formData.file ? "" : formData.driveLink?.trim() || ""
    );
    if (formData.jobOpeningTitle)
      data.append("jobOpeningTitle", formData.jobOpeningTitle);
    if (formData.jobDesignation)
      data.append("jobDesignation", formData.jobDesignation);
    if (formData.jobOpeningLocationId !== undefined)
      data.append(
        "jobOpeningLocationId",
        String(formData.jobOpeningLocationId)
      );
    if (formData.expirationDate)
      data.append(
        "expirationDate",
        dayjs(formData.expirationDate).format("YYYY-MM-DD")
      );
    if (formData.emailId) data.append("emailId", formData.emailId);

    if (formData.file) {
      data.append("file", formData.file);
    } else {
      data.append("file", "");
    }

    const isLink = formData.isLink && !(formData.file instanceof File);
    data.append("isLink", isLink ? "true" : "false");

    try {
      const response = await dashboardService.addUpdateInternalJobPostingData(
        data
      );

      if (response?.data?.statusCode === 400) {
        showToast(response?.data?.message || "Something went wrong", "error");
      } else {
        showToast(
          response?.data?.message || "Job posting saved successfully",
          "success"
        );
        onSave();
        onClose();
        setBtnLoading(true);
      }
    } catch (error) {
      showToast("Something went wrong. Please try again.", "error");
      console.error("Error saving job posting:", error);
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <CustomModal
      open={open}
      handleClose={onClose}
      title="Internal Job Posting"
      onSave={handleSaveData}
      btnLoading={btnLoading}
    >
      <Box mt={2}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FileUpload
              fileUrl={!formData.isLink ? formData.driveLink : undefined}
              isSingleSelected={true}
              isLink={formData.isLink}
              onChange={(files) => {
                if (files.length > 0) {
                  handleChange("file", files[0]);
                  handleChange("isLink", false);
                  handleChange("driveLink", "");
                  setFormErrors((prev) => ({ ...prev, driveUrl: "" }));
                } else {
                  handleChange("file", null);
                  handleChange("isLink", false);
                  setFormErrors((prev) => ({ ...prev, driveUrl: "" }));
                }
              }}
            />
          </Grid>

          {formErrors.fileError && (
            <Grid item xs={12}>
              <Typography color="error" sx={{ fontSize: "12px" }}>
                {formErrors.fileError}
              </Typography>
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ textAlign: "center" }}>
              OR
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <CustomInput
              label="Drive Link"
              type="text"
              name="driveLink"
              value={formData.isLink ?? true ? formData.driveLink : ""}
              onChange={(e) => {
                const value = e.target.value;
                handleChange("driveLink", value);
                handleChange("isLink", !!value.trim());
              }}
              placeholder="Enter Drive Link"
              error={formErrors.driveLink}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomInput
              label="Job Opening Title"
              type="text"
              name="jobOpeningTitle"
              value={formData.jobOpeningTitle}
              onChange={(e) => handleChange("jobOpeningTitle", e.target.value)}
              error={formErrors.jobOpeningTitle}
              placeholder="Enter job title"
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomInput
              label="Job Designation"
              type="text"
              name="jobDesignation"
              value={formData.jobDesignation}
              onChange={(e) => handleChange("jobDesignation", e.target.value)}
              error={formErrors.jobDesignation}
              placeholder="Enter job designation"
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomSelect
              name="jobOpeningLocationId"
              label="Job Opening Location"
              placeHolder="Select Location"
              value={formData.jobOpeningLocationId || ""}
              onChange={(event) =>
                handleChange("jobOpeningLocationId", +event.target.value)
              }
              options={locations.map((loc) => ({
                label: loc.label,
                value: loc.value,
              }))}
              required
              error={formErrors.jobOpeningLocationId}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <CustomDatePicker
              name="expirationDate"
              label="Expiration Date"
              value={
                formData.expirationDate ? dayjs(formData.expirationDate) : null
              }
              onChange={(date: Dayjs | null) =>
                handleChange(
                  "expirationDate",
                  date ? date.format("YYYY-MM-DD") : ""
                )
              }
              placeholder="Select Expiration date"
              required
              error={formErrors.expirationDate}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomInput
              label="Email ID"
              type="email"
              name="emailId"
              value={formData.emailId}
              onChange={(e) => handleChange("emailId", e.target.value)}
              error={formErrors.emailId}
              placeholder="Enter email id"
            />
          </Grid>
        </Grid>
      </Box>
    </CustomModal>
  );
};

export default InternalJobPostingModal;
